import { HtmlBlock } from '@components/display/richTexts/RichTextBlock';
import { APP_PREFIX_PATH } from '@constants/common';
import { Box, Button, Skeleton, Stack, Typography } from '@mui/material';
import { ChapterRow } from '@onlineTraining/components/display/ChapterRow';
import { LanguageSelector } from '@onlineTraining/components/display/LanguageSelector';
import { useOnlineTrainingContext } from '@onlineTraining/contexts';
import { useGetOnlineTrainingCourseQuery } from '@onlineTraining/services/onlineTraining';
import { OnlineTrainingCourseChapter } from '@onlineTraining/types';
import { NotFound } from '@routes/NotFound';
import type Player from '@vimeo/player';
import clsx from 'clsx';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams, useSearchParams } from 'react-router-dom';

export const OnlineTrainingDetail = () => {
    const id = useParams<{ id: string }>().id;
    const [searchParams, setSearchParams] = useSearchParams();
    const [chapter, setChapter] = useState(1);
    const timeParam = searchParams.get('time');
    const selectedLanguage = searchParams.get('language') ?? 'en';

    const {
        data,
        isFetching: isFetchingDetail,
        error: errorDetail,
    } = useGetOnlineTrainingCourseQuery(
        { id: Number(id), language: selectedLanguage },
        {
            skip: isNaN(Number(id)),
        }
    );

    // Language switches use a new query cache key, so `data` briefly goes undefined while
    // refetching. Keep the last successful result around so the language selector (and the
    // video iframe/player, which is never unmounted - see the video-loading effect below) stay
    // stable while a new language is being fetched.
    const [displayData, setDisplayData] = useState(data);

    // Show the skeleton for the title/trainer/description/chapters on every fetch (initial load
    // and language switches alike), so the user gets loading feedback. The language selector
    // itself is excluded - it's rendered from displayData below so it stays visible and usable.
    const showSkeleton = isFetchingDetail;

    const { setBreadcrumbs, setExtraButton } = useOnlineTrainingContext();
    const intl = useIntl();

    const iframeRef = useRef<HTMLIFrameElement>(null);
    const playerRef = useRef<Player | null>(null);
    const chaptersRef = useRef<OnlineTrainingCourseChapter[]>([]);
    const videoId = displayData?.video.id;
    // Derived rather than a plain boolean, so a video change marks the player as loading in the
    // very same render that swaps the iframe, instead of one frame later from an effect.
    const [readyVideoId, setReadyVideoId] = useState<string>();
    const videoLoading = !videoId || readyVideoId !== videoId;

    const chapters = displayData?.video.chapters ?? [];
    const hasChapters = chapters.length > 0;
    const selectedChapterNumber = hasChapters ? chapter : undefined;

    const handleLanguageChange = (code: string) => {
        setSearchParams((params) => {
            params.set('language', code);
            return params;
        });
    };

    const setChapterByTime = (seconds: number) => {
        const currentChapters = chaptersRef.current;
        if (!currentChapters.length) {
            return;
        }

        let currentChapterNumber = 1;
        for (let index = 0; index < currentChapters.length; index += 1) {
            if (seconds >= currentChapters[index].timecode) {
                currentChapterNumber = index + 1;
            }
        }

        setChapter(currentChapterNumber);
    };

    const handleChapterSelect = (chapterNumber: number, timecode: number) => {
        playerRef.current?.setCurrentTime(timecode);
        setChapter(chapterNumber);
    };

    // Keep cached course data synchronized with the latest successful query result.
    useLayoutEffect(() => {
        if (data) {
            setDisplayData(data);
        }
    }, [data]);

    // Keep the page breadcrumbs and toolbar in sync with the displayed course.
    useLayoutEffect(() => {
        if (displayData) {
            setBreadcrumbs([
                {
                    label: <FormattedMessage id="onlineTraining.title" defaultMessage="Online training" />,
                    to: APP_PREFIX_PATH + '/online-training',
                    skipParams: true,
                },
                {
                    label: displayData.title ?? '',
                    to: APP_PREFIX_PATH + `/online-training/${displayData.id}`,
                    loading: false,
                },
            ]);
            document.title = `Victron Professional - ${displayData.title}`;
        } else if (errorDetail || isNaN(Number(id))) {
            setBreadcrumbs([
                {
                    label: <FormattedMessage id="onlineTraining.title" defaultMessage="Online training" />,
                    to: APP_PREFIX_PATH + '/online-training',
                    skipParams: true,
                },
                {
                    label: <FormattedMessage id="notFound" defaultMessage="Not Found" />,
                    to: APP_PREFIX_PATH + '/online-training',
                },
            ]);
        } else {
            setBreadcrumbs([
                {
                    label: <FormattedMessage id="onlineTraining.title" defaultMessage="Online training" />,
                    to: APP_PREFIX_PATH + '/online-training',
                    skipParams: true,
                },
                {
                    label: <FormattedMessage id="loading" defaultMessage="Loading" />,
                    to: APP_PREFIX_PATH + `/online-training/${id}`,
                    loading: true,
                },
            ]);
        }

        return () => {
            setBreadcrumbs([
                {
                    label: <FormattedMessage id="onlineTraining.title" defaultMessage="Online training" />,
                    to: APP_PREFIX_PATH + '/online-training',
                    skipParams: true,
                },
            ]);
        };
    }, [displayData, errorDetail, id, intl]);

    useLayoutEffect(() => {
        if (!displayData) {
            return undefined;
        }

        setExtraButton(
            <Stack direction="row" gap={1}>
                {displayData.questionnaire_url && (
                    <Button
                        variant="contained"
                        color="primary"
                        href={displayData.questionnaire_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-center rounded-full"
                    >
                        <FormattedMessage
                            id="onlineTraining.detail.takeQuestionnaire"
                            defaultMessage="Take Questionnaire"
                        />
                    </Button>
                )}
            </Stack>
        );

        return () => setExtraButton(null);
    }, [displayData, setExtraButton]);

    // Vimeo player lifecycle for the current iframe.
    // The iframe is keyed on the video id, so every video change (e.g. a language switch) gets a
    // freshly mounted iframe and a brand new Player. player.loadVideo() is deliberately not used:
    // it resolves only when the embed answers via postMessage, and Vimeo drops that message
    // silently whenever the embed document isn't listening (for instance after the embed has
    // reloaded), which leaves the promise pending forever and the loading skeleton stuck.
    // This effect must stay a passive effect: its cleanup then runs after React has already
    // detached the old iframe, so destroy() only unregisters the player instead of ripping a
    // React-owned node out of the DOM.
    useEffect(() => {
        const iframe = iframeRef.current;
        if (!videoId || !iframe) {
            return undefined;
        }

        let cancelled = false;
        let player: Player | null = null;

        import('@vimeo/player').then(({ default: Player }) => {
            if (cancelled) {
                return;
            }

            player = new Player(iframe);
            playerRef.current = player;
            player.on('timeupdate', ({ seconds }: { seconds: number }) => {
                setChapterByTime(seconds);
            });
            player
                .ready()
                .catch(() => undefined)
                .then(() => {
                    if (!cancelled) {
                        setReadyVideoId(videoId);
                    }
                });
        });

        return () => {
            cancelled = true;

            if (!player) {
                return;
            }

            if (playerRef.current === player) {
                playerRef.current = null;
            }

            player.off('timeupdate');

            // Only destroy once React no longer owns the iframe - destroy() removes the element
            // from the DOM itself, which would break React's bookkeeping for a still mounted node
            // (as happens with StrictMode's simulated remount).
            if (!iframe.isConnected) {
                player.destroy().catch(() => undefined);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [videoId]);

    useEffect(() => {
        chaptersRef.current = chapters;
    }, [chapters]);

    // Seek to the URL time once the player is ready. Invalid and out-of-range values fall back
    // to the beginning of the video.
    useEffect(() => {
        if (timeParam === null || videoLoading || !videoId) {
            return undefined;
        }

        const player = playerRef.current;
        if (!player) {
            return undefined;
        }

        let cancelled = false;
        const requestedTime = Number(timeParam);

        player
            .getDuration()
            .then((duration) => {
                if (cancelled) {
                    return undefined;
                }

                const seekTime =
                    Number.isFinite(requestedTime) && requestedTime >= 0 && requestedTime <= duration
                        ? requestedTime
                        : 0;

                setChapterByTime(seekTime);

                return player.setCurrentTime(seekTime);
            })
            .catch((error: unknown) => {
                if (!cancelled) {
                    console.error('Failed to seek Vimeo player to the time specified in the URL.', error);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [timeParam, videoId, videoLoading]);

    return !errorDetail && !isNaN(Number(id)) ? (
        <>
            <title>
                {intl.formatMessage(
                    {
                        id: 'onlineTraining.detail.browserTitle',
                        defaultMessage: '{brand} - Course Detail',
                    },
                    { brand: 'Victron Professional' }
                )}
            </title>
            <Stack p={{ xs: 2, md: 4 }} direction={{ xs: 'column', md: 'row' }} spacing="17px">
                <Box
                    borderRadius="8px"
                    border={1}
                    borderColor="rgba(0,0,0,0.16)"
                    bgcolor="white.main"
                    width={{ xs: '100%', md: hasChapters || showSkeleton ? '60%' : '100%' }}
                >
                    {/* videoLoading (not showSkeleton) controls this overlay - the iframe is
                    remounted per video id; see the video-loading effect above. */}
                    <Box position="relative" sx={{ aspectRatio: '16 / 9' }}>
                        {videoLoading && (
                            <Skeleton
                                variant="rounded"
                                className="absolute inset-0 rounded-b-none"
                                sx={{ width: '100%', height: '100%' }}
                            />
                        )}
                        <iframe
                            key={videoId}
                            ref={iframeRef}
                            src={videoId ? `https://player.vimeo.com/video/${videoId}` : undefined}
                            title={displayData?.title}
                            allow="autoplay; fullscreen; picture-in-picture"
                            allowFullScreen
                            className={clsx('absolute inset-0 h-full w-full rounded-t-lg', {
                                invisible: videoLoading,
                            })}
                        />
                    </Box>
                    <Box p={2}>
                        {showSkeleton ? (
                            <>
                                <Stack
                                    useFlexGap
                                    direction="row"
                                    spacing={2}
                                    mb={2}
                                    flexWrap="wrap"
                                    justifyContent="space-between"
                                >
                                    <Box maxWidth={553} width="100%">
                                        <Skeleton variant="text">
                                            <Typography fontFamily="museo-sans" fontSize={32} fontWeight={600}>
                                                Placeholder title
                                            </Typography>
                                        </Skeleton>
                                        <Skeleton variant="text" width={200}>
                                            <Typography fontSize={16} fontWeight={500}>
                                                Placeholder trainer
                                            </Typography>
                                        </Skeleton>
                                    </Box>
                                    <LanguageSelector
                                        displayData={displayData}
                                        selectedLanguage={selectedLanguage}
                                        onSelect={handleLanguageChange}
                                    />
                                </Stack>
                                <Skeleton variant="text">
                                    <Typography fontSize={16}>Placeholder description line one</Typography>
                                </Skeleton>
                                <Skeleton variant="text">
                                    <Typography fontSize={16}>Placeholder description line two</Typography>
                                </Skeleton>
                                <Skeleton variant="text" width="60%">
                                    <Typography fontSize={16}>Placeholder description line three</Typography>
                                </Skeleton>
                            </>
                        ) : (
                            <>
                                <Stack
                                    useFlexGap
                                    direction="row"
                                    spacing={2}
                                    mb={2}
                                    flexWrap="wrap"
                                    justifyContent="space-between"
                                >
                                    <Box maxWidth={553}>
                                        <Typography fontFamily="museo-sans" fontSize={32} fontWeight={600}>
                                            {displayData?.title}
                                        </Typography>
                                        <Typography fontSize={16} fontWeight={500} color="rgba(0,0,0,0.4)">
                                            <FormattedMessage
                                                id="onlineTraining.byTrainer"
                                                defaultMessage="By {trainers}"
                                                values={{ trainers: displayData?.trainers }}
                                            />
                                        </Typography>
                                    </Box>
                                    <LanguageSelector
                                        displayData={displayData}
                                        selectedLanguage={selectedLanguage}
                                        onSelect={handleLanguageChange}
                                    />
                                </Stack>
                                <HtmlBlock
                                    htmlString={displayData?.description ?? ''}
                                    className="whitespace-pre-line"
                                />
                            </>
                        )}
                    </Box>
                </Box>
                {(hasChapters || showSkeleton) && (
                    <Box
                        borderRadius={2}
                        border={1}
                        borderColor="rgba(0,0,0,0.16)"
                        bgcolor="white.main"
                        width={{ xs: '100%', md: '40%' }}
                        height="fit-content"
                        p={2}
                    >
                        <Typography fontFamily="museo-sans" fontSize={16} fontWeight={400}>
                            <FormattedMessage id="onlineTraining.detail.chapters" defaultMessage="Chapters" />
                        </Typography>
                        <Stack
                            direction="column"
                            mt={2}
                            divider={<Box borderBottom={1} borderColor="rgba(0,0,0,0.16)" />}
                            borderRadius="8px"
                        >
                            {showSkeleton
                                ? Array.from({ length: 5 }).map((_, index) => (
                                      <Skeleton key={index} variant="rounded" height={64} sx={{ mb: 1 }} />
                                  ))
                                : chapters.map((chapter, index) => (
                                      <ChapterRow
                                          key={`${chapter.timecode}-${index}`}
                                          chapterNumber={index + 1}
                                          chapter={chapter}
                                          isActive={selectedChapterNumber === index + 1}
                                          topBorder={index === 0}
                                          bottomBorder={index === chapters.length - 1}
                                          onClick={handleChapterSelect}
                                      />
                                  ))}
                        </Stack>
                    </Box>
                )}
            </Stack>
        </>
    ) : (
        <NotFound />
    );
};
